// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Operacional/CrmDashboard.tsx
//  CRM — Dashboard com KPIs, funil, evolução e desempenho
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import {
  TrendingUp, Trophy, XCircle, DollarSign, Target, Users, Clock,
  AlertTriangle, MessageSquare, Activity, BarChart3,
} from 'lucide-react';
import {
  Bar, BarChart, CartesianGrid, Legend, Line, LineChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis, FunnelChart, Funnel, Cell,
} from 'recharts';

import { useNavigate } from 'react-router-dom';
import { useCrmDashboard } from '@/features/operacional/hooks/useCrmDashboard';
import type { DesempenhoVendedorComNome } from '@/features/operacional/services/crmDashboardService';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';

const fmt    = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtPct = (v: number) => `${v.toFixed(1)}%`;
const MESES_NOMES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const CrmDashboardPage = () => {
  const [meses, setMeses]           = useState(12);
  const [diasAtraso, setDiasAtraso] = useState(1);
  const { data, isLoading } = useCrmDashboard(meses, diasAtraso);
  const navigate = useNavigate();
  const [verTodosVend, setVerTodosVend]       = useState(false);
  const [verTodosAlertas, setVerTodosAlertas] = useState(false);

  if (isLoading || !data) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-28" />)}
        </div>
        <Skeleton className="h-72" />
      </div>
    );
  }

  const { kpis, funil, evolucaoGanhos, vendedores, origens, alertas } = data;

  const dadosEvolucao = evolucaoGanhos.map((e) => ({
    label:   `${MESES_NOMES[e.mes - 1]}/${String(e.ano).slice(-2)}`,
    valor:   e.valor,
    qtd:     e.quantidade,
  }));

  const dadosFunil = funil.map((f) => ({ name: f.estagioNome, value: f.quantidade, valor: f.valor, fill: f.cor ?? '#3B82F6' }));

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <BarChart3 className="h-7 w-7 text-primary" /> Dashboard CRM
          </h1>
          <p className="text-muted-foreground">Visão consolidada de oportunidades, atendimentos e performance</p>
        </div>
        <div className="flex gap-2">
          <Select value={String(meses)} onValueChange={(v) => setMeses(Number(v))}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="3">Últimos 3 meses</SelectItem>
              <SelectItem value="6">Últimos 6 meses</SelectItem>
              <SelectItem value="12">Últimos 12 meses</SelectItem>
              <SelectItem value="24">Últimos 24 meses</SelectItem>
            </SelectContent>
          </Select>
          <Select value={String(diasAtraso)} onValueChange={(v) => setDiasAtraso(Number(v))}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="0">Alertar hoje</SelectItem>
              <SelectItem value="1">≥ 1 dia atraso</SelectItem>
              <SelectItem value="3">≥ 3 dias atraso</SelectItem>
              <SelectItem value="7">≥ 7 dias atraso</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard icon={<DollarSign className="h-4 w-4" />} title="Pipeline em aberto"
          value={fmt(kpis.valorPipelineAberto)} sub={`${kpis.oportunidadesAbertas} oportunidades`} accent="text-primary" />
        <KpiCard icon={<Trophy className="h-4 w-4" />} title="Ganhos no mês"
          value={fmt(kpis.valorGanhoMes)} sub={`${kpis.oportunidadesGanhas} totais`} accent="text-emerald-500" />
        <KpiCard icon={<Target className="h-4 w-4" />} title="Taxa de conversão"
          value={fmtPct(Number(kpis.taxaConversao))} sub={`${kpis.oportunidadesPerdidas} perdas`} accent="text-blue-500" />
        <KpiCard icon={<Activity className="h-4 w-4" />} title="Ciclo médio de venda"
          value={`${kpis.cicloVendaDias} dias`} sub={`Ticket médio: ${fmt(kpis.ticketMedio)}`} accent="text-amber-500" />
        <KpiCard icon={<MessageSquare className="h-4 w-4" />} title="Atendimentos"
          value={`${kpis.totalAtendimentos}`} sub={`${kpis.atendimentosPendentes} pendentes`} accent="text-cyan-500" />
        <KpiCard icon={<Clock className="h-4 w-4" />} title="Retornos atrasados"
          value={`${kpis.retornosAtrasados}`} sub="follow-ups vencidos"
          accent={kpis.retornosAtrasados > 0 ? 'text-rose-500' : 'text-muted-foreground'} />
        <KpiCard icon={<XCircle className="h-4 w-4" />} title="Oportunidades perdidas"
          value={`${kpis.oportunidadesPerdidas}`} sub="ciclo total" accent="text-rose-500" />
        <KpiCard icon={<Users className="h-4 w-4" />} title="Total de oportunidades"
          value={`${kpis.totalOportunidades}`} sub="histórico" accent="text-foreground" />
      </div>

      {/* Funil + Evolução */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><TrendingUp className="h-5 w-5" /> Funil por estágio</CardTitle>
          </CardHeader>
          <CardContent>
            {dadosFunil.length === 0 ? (
              <p className="text-sm text-muted-foreground py-12 text-center">Sem oportunidades para exibir.</p>
            ) : (
              <div className="flex gap-2">
                <div className="flex-1 min-w-0">
                  <ResponsiveContainer width="100%" height={300}>
                    <FunnelChart>
                      <Tooltip
                        formatter={(value: number, _n, p: any) => [
                          `${value} oportunidades — ${fmt(p?.payload?.valor ?? 0)}`,
                          p?.payload?.name,
                        ]}
                      />
                      <Funnel dataKey="value" data={dadosFunil} isAnimationActive>
                        {dadosFunil.map((d, i) => <Cell key={i} fill={d.fill} />)}
                      </Funnel>
                    </FunnelChart>
                  </ResponsiveContainer>
                </div>
                {/* Nomes em HTML, fora do SVG: o LabelList do recharts ancora o texto na base do
                    trapézio (cai em cima da barra) e herda o contorno branco dele (some no fundo claro).
                    As faixas do funil ocupam de 5px a 285px dos 300px do gráfico; a lista acompanha. */}
                <ul
                  className="w-32 shrink-0 grid text-xs text-foreground"
                  style={{ height: 280, marginTop: 5, gridTemplateRows: `repeat(${dadosFunil.length}, minmax(0, 1fr))` }}
                >
                  {dadosFunil.map((d, i) => (
                    <li key={i} className="flex items-center gap-1.5 min-w-0" title={d.name}>
                      <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: d.fill }} />
                      <span className="truncate">{d.name}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Activity className="h-5 w-5" /> Evolução de ganhos</CardTitle>
          </CardHeader>
          <CardContent>
            {dadosEvolucao.length === 0 ? (
              <p className="text-sm text-muted-foreground py-12 text-center">Sem dados.</p>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={dadosEvolucao}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" />
                  <YAxis tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v: number) => fmt(v)} />
                  <Legend />
                  <Line type="monotone" dataKey="valor" name="Ganhos" stroke="#10B981" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Vendedores + Origens */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle>Desempenho por vendedor</CardTitle></CardHeader>
          <CardContent>
            {vendedores.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">Nenhum vendedor com oportunidades.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Vendedor</TableHead>
                    <TableHead className="text-center">Abertas</TableHead>
                    <TableHead className="text-center">Ganhas</TableHead>
                    <TableHead className="text-right">Valor ganho</TableHead>
                    <TableHead className="text-right">Conversão</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(vendedores as DesempenhoVendedorComNome[]).slice(0, verTodosVend ? undefined : 10).map((v) => (
                    <TableRow key={v.vendedorId}>
                      <TableCell className="font-medium">{v.vendedorNome ?? '(vendedor não encontrado)'}</TableCell>
                      <TableCell className="text-center">{v.oportunidadesAbertas}</TableCell>
                      <TableCell className="text-center">
                        <span className="text-emerald-600 font-medium">{v.oportunidadesGanhas}</span>
                        {' / '}
                        <span className="text-rose-600">{v.oportunidadesPerdidas}</span>
                      </TableCell>
                      <TableCell className="text-right font-mono">{fmt(v.valorGanho)}</TableCell>
                      <TableCell className="text-right">{fmtPct(v.taxaConversao)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {vendedores.length > 10 && (
              <Button variant="link" size="sm" className="mt-2 px-0" onClick={() => setVerTodosVend((v) => !v)}>
                {verTodosVend ? 'Mostrar só os 10 primeiros' : `Ver todos (${vendedores.length})`}
              </Button>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Origem dos leads</CardTitle></CardHeader>
          <CardContent>
            {origens.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">Sem origens cadastradas.</p>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={origens}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="origem" />
                  <YAxis />
                  <Tooltip
                    formatter={(value: number, name: string) =>
                      name === 'valor' ? fmt(value) : value}
                  />
                  <Legend />
                  <Bar dataKey="quantidade" name="Qtd" fill="#3B82F6" />
                  <Bar dataKey="valor" name="Valor" fill="#10B981" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Alertas */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" /> Alertas e pendências
            <Badge variant="secondary">{alertas.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {alertas.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Tudo em dia ✨</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Descrição</TableHead>
                  <TableHead>Referência</TableHead>
                  <TableHead className="text-right">Atraso</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {alertas.slice(0, verTodosAlertas ? undefined : 20).map((a, i) => (
                  <TableRow key={i} className="cursor-pointer" title="Abrir"
                    onClick={() => navigate(a.atendimentoId
                      ? `/operacional/crm/${a.atendimentoId}`
                      : '/operacional/crm-pipeline')}>
                    <TableCell>
                      <Badge className={
                        a.tipo === 'retorno_atrasado'    ? 'bg-amber-500/10 text-amber-600' :
                        a.tipo === 'previsao_estourada'  ? 'bg-rose-500/10 text-rose-600' :
                        'bg-muted'
                      }>
                        {a.tipo.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-sm">{a.titulo}</div>
                      <div className="text-xs text-muted-foreground">{a.descricao}</div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {a.dtReferencia ? new Date(a.dtReferencia).toLocaleDateString('pt-BR') : '—'}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {a.diasAtraso}d
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {alertas.length > 20 && (
            <Button variant="link" size="sm" className="mt-2 px-0" onClick={() => setVerTodosAlertas((v) => !v)}>
              {verTodosAlertas ? 'Mostrar só os 20 primeiros' : `Ver todos (${alertas.length})`}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

interface KpiCardProps {
  icon: React.ReactNode;
  title: string;
  value: string;
  sub: string;
  accent: string;
}

const KpiCard = ({ icon, title, value, sub, accent }: KpiCardProps) => (
  <Card>
    <CardHeader className="pb-2">
      <CardTitle className="text-xs font-medium text-muted-foreground flex items-center gap-2">
        {icon} {title}
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div className={`text-xl font-bold ${accent}`}>{value}</div>
      <p className="text-xs text-muted-foreground mt-1">{sub}</p>
    </CardContent>
  </Card>
);

export default CrmDashboardPage;
